import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatDateDDMMYY } from '../../../components/common/dateUtils';

const NegativeBalanceReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/report/negative-balance-report`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setReportData(data.data);
      } else {
        toast.error(data.error || 'Failed to generate report');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Server Error');
    }
    setLoading(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Auto-load report on component mount
  useEffect(() => {
    handleGenerateReport();
  }, []);

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 print:hidden">
        <h2 className="text-lg font-medium text-gray-800">Buyer With Negative Balance Report</h2>
        <div className="flex gap-2">
          <button
            onClick={handleGenerateReport}
            disabled={loading}
            className="px-3 py-1 text-xs bg-[#3899cf] text-white rounded hover:bg-[#0e8dc6] disabled:opacity-50 font-medium"
          >
            {loading ? 'Loading...' : 'Refresh Report'}
          </button>
          <button
            onClick={handlePrint}
            className="px-3 py-1 text-xs bg-[#3899cf] text-white rounded hover:bg-[#0e8dc6] font-medium"
          >
            Print Report
          </button>
        </div>
      </div>

      {/* Report Content */}
      {reportData && (
        <div className="bg-white border rounded border-gray-200 overflow-hidden">
          {/* Report Header */}
          <div className="bg-gray-100 p-3 border-b">
            <h3 className="text-lg font-bold text-center mb-1">
              Buyer With Negative Balance As On {formatDateDDMMYY(reportData.report_date)}
            </h3>
            <div className="text-xs text-center text-gray-600">
              Total Buyers with Negative Balance: {reportData.total_count} | 
              Total Negative Amount: <span className="text-red-600 font-semibold">{formatCurrency(reportData.total_negative_balance)}</span>
            </div>
          </div>

          {/* Report Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap border border-gray-300">
                    Name of the Buyer
                  </th>
                  <th className="px-3 py-2 text-right font-medium whitespace-nowrap border border-gray-300">
                    Negative Balance (₹)
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData.buyers.length === 0 ? (
                  <tr>
                    <td colSpan="2" className="px-3 py-4 text-center text-gray-500 border border-gray-300">
                      No buyers found with negative balance
                    </td>
                  </tr>
                ) : (
                  reportData.buyers.map((buyer, index) => (
                    <tr key={buyer.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 border border-gray-300">
                        {buyer.name}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold whitespace-nowrap border border-gray-300 text-red-600">
                        {formatCurrency(buyer.negative_balance)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {reportData.buyers.length > 0 && (
                <tfoot className="bg-gray-100">
                  <tr>
                    <td className="px-3 py-2 font-semibold border border-gray-300">
                      Total
                    </td>
                    <td className="px-3 py-2 text-right font-semibold border border-gray-300 text-red-600">
                      {formatCurrency(reportData.total_negative_balance)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={false} 
        newestOnTop 
        closeOnClick 
        pauseOnFocusLoss 
        draggable 
        pauseOnHover 
      />
    </div>
  );
};

export default NegativeBalanceReport;
